import React, { useState } from 'react';
import {
  Trophy,
  Gamepad2,
  Rocket,
  Target,
  KeySquare,
  ChevronDown,
  BookOpen,
} from 'lucide-react';
import QuestionDialog from './QuestionDialog';

export const LoadingSpinner = () => (
  <div className="flex items-center justify-center w-full h-full">
    <div className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const LandingPage = ({ onStartGame, isLoading, error, scores = [], questions = [] }) => {
  const [isHowToPlayOpen, setIsHowToPlayOpen] = useState(false);
  const [isPracticeOpen, setIsPracticeOpen] = useState(false);
  
  return (
    <div className="w-full max-w-[1000px] mx-auto bg-black bg-opacity-50 rounded-lg p-4 md:p-8">
      <div className="flex flex-col items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg md:text-2xl text-blue-300 mb-2">
            中目黒スマイル英語教室
          </h2>
          <h1 className="text-4xl md:text-7xl font-bold text-white mb-8 title-glow">
            Word Hunter
          </h1>
        </div>

        {/* Leaderboard Section */}
        <div className="w-full max-w-2xl bg-gray-900 bg-opacity-90 p-4 md:p-8 rounded-xl mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <Trophy className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0 text-yellow-500" />
            トップ10プレイヤー
          </h2>
          <div className="overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="text-gray-400">
                  <th className="pb-4 text-left w-16">順位</th>
                  <th className="pb-4 text-left">名前</th>
                  <th className="pb-4 text-left">クラス</th>
                  <th className="pb-4 text-right w-24">スコア</th>
                </tr>
              </thead>
              <tbody>
                {scores.slice(0, 10).map((score, index) => (
                  <tr key={index} className="text-gray-200 border-t border-gray-800">
                    <td className="py-3 text-left">{index + 1}</td>
                    <td className="py-3 text-left font-medium">{score.name}</td>
                    <td className="py-3 text-left">{score.class}</td>
                    <td className="py-3 text-right font-bold text-yellow-400">{score.score}</td>
                  </tr>
                ))}
                {scores.length === 0 && (
                  <tr className="text-gray-400">
                    <td colSpan="4" className="py-3 text-center">まだスコアがありません</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="w-full max-w-2xl bg-gray-900 bg-opacity-90 p-4 md:p-8 rounded-xl mb-8">
          {/* Title with expand/collapse button */}
          <button 
            onClick={() => setIsHowToPlayOpen(!isHowToPlayOpen)}
            className="w-full text-left hover:opacity-80 transition-opacity"
          >
            <h2 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center justify-between">
              <span className="flex items-center gap-3">
                <Gamepad2 className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
                遊び方
              </span>
              <ChevronDown 
                className={`w-6 h-6 transition-transform duration-300 ${isHowToPlayOpen ? 'rotate-180' : ''}`}
              />
            </h2>
          </button>

          {/* Instructions */}
          <div 
            className={`space-y-4 md:space-y-6 text-sm md:text-base text-gray-200 overflow-hidden transition-all duration-300 ease-in-out ${
              isHowToPlayOpen ? 'max-h-[1000px] opacity-100 mt-6' : 'max-h-0 opacity-0'
            }`}
          >
            {/* Main Instruction */}
            <div className="flex gap-3">
              <Target className="w-5 h-5 flex-shrink-0 mt-1 text-blue-400" />
              <p>空欄に当てはまる正しい単語を撃って、文章を完成させましょう。</p>
            </div>
            <div className="flex gap-3">
              <p>正しい単語をミスするか、ライフ♡がなくなると、ゲームオーバーだよ</p>
            </div>
            {/* Controls Section */}
            <div className="flex gap-3">
              <KeySquare className="w-5 h-5 flex-shrink-0 mt-1 text-blue-400" />
              <div className="flex-1">
                <div className="controls">
                  <p className="font-bold mb-2">操作方法：</p>
                </div>
                <ul className="space-y-1 ml-4">
                  {/* Desktop Controls */}
                  <li className="flex items-center font-bold text-blue-400 mt-2">デスクトップ:</li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-gray-200 rounded-full mr-2"></span>
                    ← → 矢印キーで宇宙船を動かす
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-gray-200 rounded-full mr-2"></span>
                    スペースキーで発射
                  </li>

                  {/* Touch Controls */}
                  <li className="flex items-center font-bold text-blue-400 mt-2">タッチスクリーン:</li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-gray-200 rounded-full mr-2"></span>
                    指でドラッグして宇宙船を動かす
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-gray-200 rounded-full mr-2"></span>
                    画面をタップして発射
                  </li>
                </ul>
              </div>
            </div>

            {/* Scoring Section */}
            <div className="flex gap-3">
              <Trophy className="w-5 h-5 flex-shrink-0 mt-1 text-yellow-500" />
              <div className="flex-1">
                <div className="controls">
                  <p className="font-bold mb-2">得点システム：</p>
                </div>
                <ul className="space-y-1 ml-4">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-gray-200 rounded-full mr-2"></span>
                    正解で+100ポイント
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-gray-200 rounded-full mr-2"></span>
                    ３回以上連続正解でコンボ : ボーナス50ポイント
                  </li>

                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-gray-200 rounded-full mr-2"></span>
                    不正解で-50ポイント
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-gray-200 rounded-full mr-2"></span>
                    エイリアン船を撃ち落とすと50ポイント
                  </li>

                </ul>
              </div>
            </div>
          </div>
        </div>

        {error ? (
          <div className="text-red-500 text-center mb-4">
            問題の読み込みエラー: {error}
          </div>
        ) : null}
        
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => {
                setIsPracticeOpen(true);
              }}
              disabled={isLoading}
              className={`px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg text-base md:text-xl font-bold
                       flex items-center justify-center gap-2 ${
                         isLoading
                           ? 'opacity-50 cursor-not-allowed'
                           : 'hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105'
                       }
                       focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-opacity-50`}
            >
              <BookOpen className="w-5 h-5 md:w-6 md:h-6" />
             まずは練習問題！
            </button>
            <button
              onClick={onStartGame}
              disabled={isLoading}
              className={`px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-base md:text-xl font-bold
                       flex items-center justify-center gap-2 ${
                         isLoading
                           ? 'opacity-50 cursor-not-allowed'
                           : 'hover:from-blue-600 hover:to-blue-700 transition-all transform hover:scale-105'
                       }
                       focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50`}
            >
              <Rocket className="w-5 h-5 md:w-6 md:h-6" />
              {isLoading ? '読み込み中...' : 'ゲームを始める'}
            </button>

          </div>
        )}
        {/* Question Dialog */}
        <QuestionDialog
          questions={questions}
          isOpen={isPracticeOpen}
          onClose={() => setIsPracticeOpen(false)}
        />
      </div>
    </div>
  );
};

export default LandingPage;